import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  Brain, VideoCamera, Lightning, Robot, Eye, Check, X,
  Warning, ShieldWarning, ArrowClockwise, Play, Lightbulb,
  Drop, Fire, Leaf, ChartLineUp, Plus
} from '@phosphor-icons/react';

const SEVERITY_COLORS = { info: '#3B82F6', warning: '#F59E0B', critical: '#EF4444' };
const CATEGORY_ICONS = { electricity: Lightning, water: Drop, gas: Fire, appliance: Lightbulb, climate: Lightbulb, lighting: Lightbulb, general: Leaf, security: ShieldWarning, comfort: Lightbulb, energy: Lightning };

export default function AIIntelligencePage() {
  const { api } = useAuth();
  const [tab, setTab] = useState('cameras');
  const [cameraAlerts, setCameraAlerts] = useState([]);
  const [cameraStats, setCameraStats] = useState(null);
  const [energySuggestions, setEnergySuggestions] = useState([]);
  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState('');
  const [cameras, setCameras] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const requests = [
        api.get('/ai-intelligence/camera-alerts'),
        api.get('/ai-intelligence/camera-stats'),
        api.get('/ai-intelligence/energy-suggestions'),
        api.get('/ai-intelligence/predictions'),
        api.get('/cameras'),
      ];
      const [alertsRes, statsRes, energyRes, predRes, camRes] = await Promise.all(requests);
      setCameraAlerts(alertsRes.data);
      setCameraStats(statsRes.data);
      setEnergySuggestions(energyRes.data.suggestions || []);
      setPredictions(predRes.data.predictions || []);
      setCameras(camRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const analyzeCamera = async (camId) => {
    setAnalyzing(camId);
    try {
      await api.post(`/ai-intelligence/camera-analyze/${camId}`);
      fetchData();
    } catch (e) { console.error(e); }
    setAnalyzing('');
  };

  const reviewAlert = async (alertId) => {
    try {
      await api.put(`/ai-intelligence/camera-alerts/${alertId}/review`);
      fetchData();
    } catch (e) { console.error(e); }
  };

  const acceptPrediction = async (pred) => {
    try {
      await api.post(`/ai-intelligence/predictions/${pred.id}/accept`, { rule_template: pred.rule_template });
      alert(`Rule "${pred.rule_template.name}" created!`);
      fetchData();
    } catch (e) { alert('Failed: ' + (e.response?.data?.detail || e.message)); }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-32">
      <div className="w-8 h-8 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const TABS = [
    { id: 'cameras', label: 'Camera AI', icon: VideoCamera, count: cameraStats?.unreviewed || 0 },
    { id: 'energy', label: 'Energy', icon: Lightning, count: energySuggestions.length },
    { id: 'predictions', label: 'Predictions', icon: Brain, count: predictions.length },
  ];

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="ai-intelligence-page">
      <div>
        <h1 className="font-heading text-xl sm:text-2xl font-bold tracking-tight">AI Intelligence</h1>
        <p className="text-xs sm:text-sm text-muted-foreground mt-1">Camera monitoring, energy optimization, and predictive automation</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1.5 sm:gap-2 overflow-x-auto no-scrollbar">
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`ai-tab-${t.id}`}
              className={`px-3 sm:px-4 py-2 sm:py-2.5 rounded-xl sm:rounded-2xl text-xs sm:text-sm font-semibold flex items-center gap-1.5 sm:gap-2 transition-all whitespace-nowrap flex-shrink-0 ${tab === t.id ? 'bg-[#FF6B35] text-white' : 'bg-white/5 text-muted-foreground hover:bg-white/10'}`}>
              <Icon size={16} /> {t.label}
              {t.count > 0 && (
                <span className="w-5 h-5 rounded-full text-[10px] flex items-center justify-center font-bold"
                  style={{ background: tab === t.id ? 'rgba(255,255,255,0.25)' : 'rgba(255,107,53,0.2)', color: tab === t.id ? '#fff' : '#FF6B35' }}>
                  {t.count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Camera AI Tab */}
      {tab === 'cameras' && (
        <div className="space-y-4" data-testid="camera-ai-section">
          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
            {[
              { label: 'Cameras', value: cameraStats?.cameras || 0, color: '#3B82F6' },
              { label: 'Total Alerts', value: cameraStats?.total_alerts || 0, color: '#FF6B35' },
              { label: 'Unreviewed', value: cameraStats?.unreviewed || 0, color: '#F59E0B' },
              { label: 'Warnings', value: cameraStats?.warnings || 0, color: '#EF4444' },
            ].map(s => (
              <div key={s.label} className="rounded-2xl p-4 text-center"
                style={{ background: `${s.color}08`, border: `1px solid ${s.color}20` }}>
                <p className="text-2xl font-bold" style={{ color: s.color }}>{s.value}</p>
                <p className="text-[10px] text-muted-foreground uppercase tracking-widest">{s.label}</p>
              </div>
            ))}
          </div>

          {/* Quick Analyze */}
          {cameras.length > 0 && (
            <div className="rounded-2xl p-4" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
              <p className="text-xs font-semibold text-muted-foreground uppercase mb-3">Quick Analyze</p>
              <div className="flex gap-2 flex-wrap">
                {cameras.map(cam => (
                  <button key={cam.camera_id} onClick={() => analyzeCamera(cam.camera_id)}
                    disabled={analyzing === cam.camera_id}
                    data-testid={`analyze-cam-${cam.camera_id}`}
                    className="px-4 py-2 rounded-xl text-xs font-semibold flex items-center gap-2 transition-colors"
                    style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
                    <VideoCamera size={12} />
                    {analyzing === cam.camera_id ? 'Analyzing...' : cam.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Alerts */}
          {cameraAlerts.length === 0 ? (
            <div className="text-center py-12 rounded-2xl" style={{ background: 'rgba(255,255,255,0.02)' }}>
              <Eye size={36} className="text-neutral-600 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No camera alerts yet. Analyze a camera to get AI insights.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {cameraAlerts.map(alert => (
                <div key={alert.alert_id} className={`rounded-2xl p-4 ${alert.reviewed ? 'opacity-50' : ''}`}
                  data-testid={`camera-alert-${alert.alert_id}`}
                  style={{ background: 'rgba(255,255,255,0.03)', borderLeft: `3px solid ${SEVERITY_COLORS[alert.severity] || '#737373'}` }}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <VideoCamera size={18} style={{ color: SEVERITY_COLORS[alert.severity] }} className="mt-0.5" />
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold">{alert.camera_name}</p>
                          <span className="text-[9px] px-2 py-0.5 rounded-full font-bold uppercase"
                            style={{ background: `${SEVERITY_COLORS[alert.severity]}20`, color: SEVERITY_COLORS[alert.severity] }}>
                            {alert.severity}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">{alert.room} — {new Date(alert.timestamp).toLocaleString()}</p>
                        <p className="text-sm text-neutral-300 mt-2 leading-relaxed">{alert.analysis}</p>
                      </div>
                    </div>
                    {!alert.reviewed && (
                      <button onClick={() => reviewAlert(alert.alert_id)}
                        className="p-2 rounded-xl hover:bg-white/5 text-muted-foreground hover:text-green-400 flex-shrink-0">
                        <Check size={16} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Energy Tab */}
      {tab === 'energy' && (
        <div className="space-y-3" data-testid="energy-section">
          {energySuggestions.map(s => {
            const Icon = CATEGORY_ICONS[s.category] || Leaf;
            const typeColors = { warning: '#EF4444', optimization: '#F59E0B', tip: '#22C55E' };
            return (
              <div key={s.id} className="rounded-2xl p-5" data-testid={`energy-suggestion-${s.id}`}
                style={{ background: 'rgba(255,255,255,0.03)', borderLeft: `3px solid ${typeColors[s.type] || '#737373'}` }}>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: `${typeColors[s.type]}15` }}>
                    <Icon size={20} style={{ color: typeColors[s.type] }} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold">{s.title}</p>
                      <span className="text-[9px] px-2 py-0.5 rounded-full font-bold uppercase"
                        style={{ background: `${typeColors[s.type]}15`, color: typeColors[s.type] }}>
                        {s.priority}
                      </span>
                    </div>
                    <p className="text-sm text-neutral-400 mt-1">{s.description}</p>
                    <p className="text-xs text-[#22C55E] font-semibold mt-2">{s.savings}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Predictions Tab */}
      {tab === 'predictions' && (
        <div className="space-y-3" data-testid="predictions-section">
          {predictions.length === 0 ? (
            <div className="text-center py-12 rounded-2xl" style={{ background: 'rgba(255,255,255,0.02)' }}>
              <Brain size={36} className="text-neutral-600 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No predictions yet. Use more automation features to generate patterns.</p>
            </div>
          ) : predictions.map(pred => {
            const CatIcon = CATEGORY_ICONS[pred.category] || Robot;
            return (
              <div key={pred.id} className="rounded-2xl p-5" data-testid={`prediction-${pred.id}`}
                style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(255,107,53,0.1)' }}>
                    <CatIcon size={20} className="text-[#FF6B35]" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold">{pred.title}</p>
                      <span className="text-[9px] px-2 py-0.5 rounded-full font-bold"
                        style={{ background: 'rgba(255,107,53,0.15)', color: '#FF6B35' }}>
                        {pred.confidence}% confidence
                      </span>
                    </div>
                    <p className="text-sm text-neutral-400 mt-1">{pred.description}</p>
                    <div className="flex items-center gap-2 mt-3">
                      <span className="text-[10px] text-muted-foreground uppercase">{pred.rule_template?.room}</span>
                      <span className="text-neutral-600">|</span>
                      <span className="text-[10px] text-muted-foreground">{pred.rule_template?.actions?.length || 0} actions</span>
                    </div>
                  </div>
                  <button onClick={() => acceptPrediction(pred)} data-testid={`accept-prediction-${pred.id}`}
                    className="px-4 py-2 rounded-xl bg-[#FF6B35] text-white text-xs font-bold flex items-center gap-1.5 flex-shrink-0">
                    <Plus size={12} /> Accept
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
